import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { BillingBooleanSettingField } from '@/components/customers/BillingBooleanSettingField'
import {
  HouseAccountSettingsSection,
  getPaymentTermsDisabledTooltip,
  isHouseAccountEnabled,
  setHouseAccountEnabled,
} from '@/components/customers/HouseAccountSettingsSection'
import SaveBar from '@/components/forms/SaveBar'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import {
  organizationBillingSettingsQueries,
  useOrganizationBillingSettings,
  useResetOrganizationBillingSettingsMutation,
  useUpdateOrganizationBillingSettingsMutation,
} from '@/lib/services/organization-billing-settings/organization-billing-settings.query'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import { HandCoins, ReceiptText, RotateCcw, Truck } from 'lucide-react'
import { toast } from 'sonner'

const billingSettingIds = {
  payLaterAllowed: 'default_pay_later_allowed',
  allowFulfillmentBeforePayment: 'default_allow_fulfillment_before_payment',
  creditLimit: 'default_credit_limit',
  paymentTermsCode: 'default_payment_terms_code',
  paymentTermsDays: 'default_payment_terms_days',
}

export function getBillingSettingsFormDefaults(settings = {}) {
  const payLaterAllowed = settings[billingSettingIds.payLaterAllowed] !== false

  return {
    expected_version: settings.version,
    [billingSettingIds.payLaterAllowed]: payLaterAllowed,
    [billingSettingIds.allowFulfillmentBeforePayment]:
      payLaterAllowed && settings[billingSettingIds.allowFulfillmentBeforePayment] === true,
    [billingSettingIds.creditLimit]: settings[billingSettingIds.creditLimit] ?? '',
    [billingSettingIds.paymentTermsCode]:
      settings[billingSettingIds.paymentTermsCode] ?? CustomerPaymentTerms.DueOnReceipt.Value,
    [billingSettingIds.paymentTermsDays]: String(settings[billingSettingIds.paymentTermsDays] ?? 0),
  }
}

export function buildBillingSettingsPayload(value, settings = {}) {
  const payLaterAllowed = value[billingSettingIds.payLaterAllowed] !== false
  const allowFulfillmentBeforePayment =
    payLaterAllowed && value[billingSettingIds.allowFulfillmentBeforePayment] === true
  const creditLimit = String(value[billingSettingIds.creditLimit] ?? '').trim()
  const customDays = Math.max(
    0,
    Number.parseInt(String(value[billingSettingIds.paymentTermsDays] ?? ''), 10) || 0
  )

  return {
    expected_version: value.expected_version ?? settings.version,
    [billingSettingIds.payLaterAllowed]: payLaterAllowed,
    [billingSettingIds.allowFulfillmentBeforePayment]: allowFulfillmentBeforePayment,
    [billingSettingIds.creditLimit]: payLaterAllowed && creditLimit !== '' ? creditLimit : null,
    [billingSettingIds.paymentTermsCode]: allowFulfillmentBeforePayment
      ? value[billingSettingIds.paymentTermsCode] || CustomerPaymentTerms.DueOnReceipt.Value
      : CustomerPaymentTerms.DueOnReceipt.Value,
    [billingSettingIds.paymentTermsDays]:
      allowFulfillmentBeforePayment &&
      value[billingSettingIds.paymentTermsCode] === CustomerPaymentTerms.Custom.Value
        ? customDays
        : 0,
  }
}

function resetBillingSettingsForm(formApi, settings) {
  const values = getBillingSettingsFormDefaults(settings)

  formApi.reset(values)
  Object.entries(values).forEach(([fieldName, fieldValue]) => {
    formApi.setFieldValue(fieldName, fieldValue, { dontValidate: true })
  })
}

function getPaymentTermsOptions() {
  return Object.values(CustomerPaymentTerms).map((term) => ({
    id: term.Value,
    name: term.Name,
  }))
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/billing/')({
  component: BillingSettingsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'billing-settings')) throw notFound()

    await context.queryClient.ensureQueryData(
      organizationBillingSettingsQueries.detail({ organizationId: params.orgId })
    )

    return { isReadOnly: !context.ability.can('edit', 'billing-settings') }
  },
})

export function BillingSettingsPage() {
  const { orgId } = useParams({ strict: false })
  const { isReadOnly } = useIsReadOnly()
  const path = usePathHelper()
  const { data: settings } = useOrganizationBillingSettings({ organizationId: orgId })
  const updateMutation = useUpdateOrganizationBillingSettingsMutation()
  const resetMutation = useResetOrganizationBillingSettingsMutation()

  const form = useAppForm({
    defaultValues: getBillingSettingsFormDefaults(settings),
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return

        try {
          const savedSettings = await updateMutation.mutateAsync(
            buildBillingSettingsPayload(value, settings)
          )
          toast.success('Billing settings updated successfully')
          setTimeout(() => {
            // Mounted field components can lag behind a submit-time reset, so keep them in
            // sync with the new defaults the same tick.
            resetBillingSettingsForm(formApi, savedSettings)
          })
        } catch (error) {
          const currentSettings = error?.response?.data?.current

          if (error?.response?.status === 409 && currentSettings) {
            resetBillingSettingsForm(formApi, currentSettings)
            toast.error('These billing settings changed in another session', {
              description: 'We loaded the latest saved values. Review them before saving again.',
            })
            return
          }

          toast.error('Failed to update billing settings', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  async function resetDefaults() {
    try {
      const resetSettings = await resetMutation.mutateAsync({
        expectedVersion: form.state.values.expected_version,
      })

      resetBillingSettingsForm(form, resetSettings)
      toast.success('Billing defaults restored')
    } catch (error) {
      const currentSettings = error?.response?.data?.current

      if (error?.response?.status === 409 && currentSettings) {
        resetBillingSettingsForm(form, currentSettings)
        toast.error('These billing settings changed in another session', {
          description: 'We loaded the latest saved values. Review them before trying again.',
        })
        return
      }

      toast.error('Failed to restore billing defaults', {
        description: getErrorMessage(error),
      })
    }
  }

  const payLaterAllowed = useStore(
    form.store,
    (state) => state.values[billingSettingIds.payLaterAllowed] !== false
  )
  const allowFulfillmentBeforePayment = useStore(
    form.store,
    (state) => state.values[billingSettingIds.allowFulfillmentBeforePayment] === true
  )
  const paymentTermsCode = useStore(
    form.store,
    (state) => state.values[billingSettingIds.paymentTermsCode]
  )
  const termsDisabled = !payLaterAllowed || !allowFulfillmentBeforePayment
  const houseAccountEnabled = isHouseAccountEnabled(payLaterAllowed, allowFulfillmentBeforePayment)

  return (
    <div className="max-w-4xl space-y-5">
      <AdminPageHeader
        icon={ReceiptText}
        title="Billing Settings"
        description="Set the billing setup your shop starts with whenever a new customer is added."
        actions={
          <>
            {!isReadOnly ? (
              <AdminHeaderActionButton
                variant="outline"
                disabled={resetMutation.isPending}
                onClick={resetDefaults}
              >
                <RotateCcw className="size-4" />
                Restore defaults
              </AdminHeaderActionButton>
            ) : null}
            <RecordHistoryActionMenu
              link={path.billing.link('history')}
              triggerLabel="More billing actions"
            />
          </>
        }
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Billing settings are organization-specific. Manage billing settings inside the right
            florist account.
          </OrganizationContextNotice>
        }
      >
        <form.AppForm>
          <form.Form>
            <Card>
              <CardHeader>
                <CardTitle>Starting Billing Setup</CardTitle>
                <CardDescription>
                  We copy these choices onto each new customer. Customers already in your list keep
                  the billing setup they have today.
                </CardDescription>
              </CardHeader>
              <CardContent className="@container">
                <HouseAccountSettingsSection
                  enabled={houseAccountEnabled}
                  disabled={isReadOnly}
                  description="Choose whether newly created customers start as House Accounts. Existing customers and orders are not changed."
                  onEnabledChange={(enabled) =>
                    setHouseAccountEnabled(form, enabled, {
                      payLaterField: billingSettingIds.payLaterAllowed,
                      fulfillmentField: billingSettingIds.allowFulfillmentBeforePayment,
                      creditLimitField: billingSettingIds.creditLimit,
                      paymentTermsCodeField: billingSettingIds.paymentTermsCode,
                      paymentTermsDaysField: billingSettingIds.paymentTermsDays,
                      paymentTermsDaysDefault: '0',
                    })
                  }
                >
                  <form.AppField name={billingSettingIds.payLaterAllowed}>
                    {(field) => (
                      <BillingBooleanSettingField
                        field={field}
                        icon={HandCoins}
                        title="Allow Pay Later Orders"
                        description="Let new customers place an order before the full balance is collected."
                        disabled={isReadOnly}
                      />
                    )}
                  </form.AppField>

                  {!payLaterAllowed ? (
                    <Typography variant="supporting" wrap="pretty" className="block">
                      Turn Pay Later on before setting fulfillment-before-payment, terms, or a
                      credit limit.
                    </Typography>
                  ) : null}

                  <form.AppField name={billingSettingIds.allowFulfillmentBeforePayment}>
                    {(field) => (
                      <BillingBooleanSettingField
                        field={field}
                        icon={Truck}
                        title="Allow Fulfillment Before Payment"
                        description="Let new customers receive pickup or delivery before payment is collected."
                        disabled={isReadOnly || !payLaterAllowed}
                        displayValue={payLaterAllowed && allowFulfillmentBeforePayment}
                      />
                    )}
                  </form.AppField>

                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 @md:col-span-3">
                      <form.AppField name={billingSettingIds.creditLimit}>
                        {(field) => (
                          <field.MoneyInput
                            label="Credit Limit"
                            placeholder="No limit"
                            showFreeBadge={false}
                            formatEmptyAsZero={false}
                            disabled={isReadOnly || !payLaterAllowed}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <div className="col-span-6 @md:col-span-3">
                      <form.AppField name={billingSettingIds.paymentTermsCode}>
                        {(field) => (
                          <field.SelectInput
                            label="Payment Terms"
                            options={getPaymentTermsOptions()}
                            disabled={isReadOnly || termsDisabled}
                            disabledTooltip={getPaymentTermsDisabledTooltip(
                              payLaterAllowed,
                              allowFulfillmentBeforePayment
                            )}
                          />
                        )}
                      </form.AppField>
                    </div>
                    {paymentTermsCode === CustomerPaymentTerms.Custom.Value ? (
                      <div className="col-span-6 @md:col-span-3">
                        <form.AppField name={billingSettingIds.paymentTermsDays}>
                          {(field) => (
                            <field.TextInput
                              label="Custom Days"
                              placeholder="30"
                              inputMode="numeric"
                              disabled={isReadOnly || termsDisabled}
                            />
                          )}
                        </form.AppField>
                      </div>
                    ) : null}
                  </div>
                </HouseAccountSettingsSection>
              </CardContent>
            </Card>

            {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
          </form.Form>
        </form.AppForm>
      </IfGlobalOrg>
    </div>
  )
}
